import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { useContent } from "@/lib/content";
import { FadeUp, Eyebrow } from "@/components/Motion";
import { imgSrc } from "@/lib/api";

export default function About() {
  const content = useContent();
  if (!content) return <div className="min-h-screen bg-ivory" />;
  const about = content.about;

  return (
    <main className="pt-[72px]">
      {/* header + story */}
      <section className="py-16 sm:py-24" data-testid="about-header">
        <div className="wrap">
          <Eyebrow className="mb-6">{about.eyebrow}</Eyebrow>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <FadeUp delay={0.05}>
                <h1 className="font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl">
                  {about.title}
                </h1>
              </FadeUp>
              <div className="mt-8 space-y-5">
                {(about.story || []).map((p, i) => (
                  <FadeUp key={i} delay={0.1 + i * 0.06}>
                    <p className="max-w-xl leading-relaxed text-charcoal/70">{p}</p>
                  </FadeUp>
                ))}
              </div>
              <FadeUp delay={0.25}>
                <div className="mt-10 border-l-2 border-gold/60 pl-5">
                  <p className="font-serif text-2xl italic text-timber">{about.signature || "Lucas"}</p>
                  <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/50">
                    {about.signature_role || "Owner-Operator, Heritage Constructions"}
                  </p>
                </div>
              </FadeUp>
            </div>
            <div className="lg:col-span-5">
              <FadeUp delay={0.15}>
                <div className="photo-frame aspect-[4/5] rounded-lg" data-testid="about-image">
                  <img src={imgSrc(about.image)} alt="Timber framing by Heritage Constructions" />
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* values */}
      <section className="border-y border-charcoal/10 bg-stone/40 py-20 sm:py-28" data-testid="about-values">
        <div className="wrap">
          <Eyebrow className="mb-6">What we hold to</Eyebrow>
          <FadeUp delay={0.05}>
            <h2 className="mb-14 max-w-xl font-serif text-3xl leading-tight text-charcoal sm:text-4xl lg:text-5xl">
              The standards the work is judged by.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-charcoal/10 bg-charcoal/10 sm:grid-cols-2 lg:grid-cols-4">
            {(about.values || []).map((v, i) => (
              <FadeUp key={v.title} delay={i * 0.07} className="bg-ivory">
                <div className="h-full p-8 transition-colors duration-500 hover:bg-paper sm:p-9">
                  <p className="font-mono text-[11px] tracking-[0.25em] text-gold">0{i + 1}</p>
                  <h3 className="mt-4 font-serif text-2xl text-charcoal">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/60">{v.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* areas */}
      <section className="py-20 sm:py-24" data-testid="about-areas">
        <div className="wrap grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Eyebrow className="mb-5">Service area</Eyebrow>
            <FadeUp delay={0.05}>
              <h2 className="font-serif text-3xl leading-tight text-charcoal sm:text-4xl">
                From the hills to the suburbs.
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-6 max-w-md leading-relaxed text-charcoal/65">
                The Yarra Valley is home base, with projects across the surrounding areas, Maroondah
                and selected Melbourne suburbs. Suitable projects further afield are welcome.
              </p>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <FadeUp delay={0.15} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(about.areas || []).map((a) => (
                <div key={a} className="rounded-md border border-charcoal/12 bg-paper px-4 py-5 text-center text-sm text-charcoal/75 transition-colors hover:border-gold/50">
                  {a}
                </div>
              ))}
            </FadeUp>
          </div>
        </div>
      </section>

      {/* cta */}
      <section className="bg-charcoal py-20 text-ivory sm:py-28">
        <div className="wrap text-center">
          <FadeUp>
            <h2 className="mx-auto max-w-2xl font-serif text-3xl leading-tight sm:text-5xl">
              The best way to judge a carpenter is a conversation.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a href="tel:+61456492849" className="btn-gold" data-testid="about-cta-phone"><Phone size={16} /> Call Lucas: 0456 492 849</a>
              <Link to="/contact" className="btn-ghost-light">Request a Quote <ArrowRight size={16} /></Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
